import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DraftSiteTier,
  IDraftRunSummary,
  useCreateContractModelDraftRunMutation,
  useListContractModelDraftRunsQuery,
} from "store/queries/contractModelDrafts";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";

import {
  formatRunDate,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
} from "./utils";

const SITE_TIER_OPTIONS: { label: string; value: DraftSiteTier }[] = [
  { label: "Office / Non-Facility (POS 11)", value: "non_facility" },
  { label: "Facility / Outpatient (POS 22+)", value: "facility" },
  { label: "Both tiers (one combined schedule)", value: "both" },
];

// Module-level table cell renderers (not defined during render).
const nameBody = (r: IDraftRunSummary) => (
  <span className="font-medium">{runDisplayName(r)}</span>
);
const statusBody = (r: IDraftRunSummary) => (
  <Tag
    value={runStatusLabel(r.status)}
    severity={runStatusSeverity(r.status)}
  />
);
const createdBody = (r: IDraftRunSummary) => formatRunDate(r.createdAt);

export function ContractModelDrafts() {
  const navigate = useNavigate();

  const { data: runs = [] } = useListContractModelDraftRunsQuery();
  const [createRun, { isLoading: creating }] =
    useCreateContractModelDraftRunMutation();
  const { data: providersData } = useGetAllProvidersQuery();
  const { data: payersData } = useGetAllPayersQuery();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [providerId, setProviderId] = useState<string | null>(null);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [siteTier, setSiteTier] = useState<DraftSiteTier | null>(null);

  const providerOptions = useMemo(
    () =>
      (providersData?.data ?? [])
        .map((p) => ({ label: `${p.name} (${p.specialty})`, value: p.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providersData]
  );
  const payerOptions = useMemo(
    () =>
      ((payersData?.data ?? []) as { id: number; name: string }[])
        .map((p) => ({ label: p.name, value: p.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [payersData]
  );

  const resetCreate = () => {
    setShowCreate(false);
    setName("");
    setProviderId(null);
    setPayerId(null);
    setSiteTier(null);
  };

  // Provider + payer + tier scope the run: the account is derived from the provider, and the tier tells
  // the model which rate column to read. Required so a run is never modeled against the wrong context.
  const canCreate = !creating && !!providerId && payerId != null && !!siteTier;

  const handleCreate = async () => {
    if (!canCreate) return;
    const created = await createRun({
      name: name.trim() || undefined,
      providerId: providerId ?? undefined,
      payerId: payerId ?? undefined,
      siteTier: siteTier ?? undefined,
    }).unwrap();
    resetCreate();
    navigate(`/contract-model-drafts/${created.id}`);
  };

  const openRun = (id: string) => navigate(`/contract-model-drafts/${id}`);

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <h1 className="m-0">AI Contract Modeling</h1>
        <Button
          label="New run"
          icon="pi pi-plus"
          onClick={() => setShowCreate(true)}
        />
      </div>

      <PageDataTable
        value={[...runs]}
        dataKey="id"
        emptyMessage="No runs yet. Click “New run” to model a contract."
        rowClassName={() => "cursor-pointer"}
        onRowClick={(e) => openRun((e.data as IDraftRunSummary).id)}
      >
        <Column header="Name" body={nameBody} />
        <Column header="Status" body={statusBody} />
        <Column header="Documents" field="documentCount" />
        <Column header="Recommendations" field="recommendationCount" />
        <Column header="Created" body={createdBody} />
      </PageDataTable>

      <Dialog
        header="New contract modeling run"
        visible={showCreate}
        style={{ width: "32rem" }}
        onHide={resetCreate}
        footer={
          <>
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={resetCreate}
            />
            <Button
              label="Create"
              icon="pi pi-check"
              disabled={!canCreate}
              onClick={handleCreate}
            />
          </>
        }
      >
        <label className="block mb-1 font-medium" htmlFor="run-provider">
          Provider
        </label>
        <Dropdown
          inputId="run-provider"
          className="w-full"
          value={providerId}
          options={providerOptions}
          filter
          placeholder="Select the provider entity"
          onChange={(e) => setProviderId(e.value)}
        />
        <small className="text-500 block mt-1 mb-3">
          The run&rsquo;s account is derived from this provider. Model one
          provider at a time &mdash; for an office/facility split, run it once
          per entity.
        </small>

        <label className="block mb-1 font-medium" htmlFor="run-payer">
          Payer
        </label>
        <Dropdown
          inputId="run-payer"
          className="w-full mb-3"
          value={payerId}
          options={payerOptions}
          filter
          placeholder="Select the payer"
          onChange={(e) => setPayerId(e.value)}
        />

        <label className="block mb-1 font-medium" htmlFor="run-tier">
          Rate tier
        </label>
        <Dropdown
          inputId="run-tier"
          className="w-full"
          value={siteTier}
          options={SITE_TIER_OPTIONS}
          placeholder="Which rate column does this provider bill?"
          onChange={(e) => setSiteTier(e.value)}
        />
        <small className="text-500 block mt-1 mb-3">
          Contracts often list separate office and facility columns; this tells
          the model which one applies to this provider.
        </small>

        <label className="block mb-1 font-medium" htmlFor="run-name">
          Name <span className="text-500 font-normal">(optional)</span>
        </label>
        <InputText
          id="run-name"
          className="w-full"
          value={name}
          placeholder="e.g. Heritage Aetna — Office"
          onChange={(e) => setName(e.target.value)}
        />
        <small className="text-500 block mt-2">
          You&rsquo;ll upload documents and generate the model on the next
          screen.
        </small>
      </Dialog>
    </div>
  );
}
